import {
  type MapSize,
  type ProcessInstance,
  SIMPLE_MAP_LIMIT,
  withinLimit,
} from "./processGraphModel";

const COMMON_PATH_NODE_WEIGHT = 1;
const COMMON_PATH_CONNECTION_WEIGHT = 2;

type Route = { nodes: Set<string>; connections: Set<string>; routed: Set<string> };

// Several scope checks walk every case's route per change; a case's route never changes.
const routes = new WeakMap<ProcessInstance, Route>();

/**
 * A case's steps and connections, entry and exit as `null`. `routed` leaves out the loops, which
 * a map draws on the cell itself rather than as a route between cells.
 */
const routeOf = (instance: ProcessInstance): Route => {
  const cached = routes.get(instance);
  if (cached) return cached;
  const sequence = instance.steps.map(step => step.nodeId);
  const route = [null, ...sequence, null];
  const pairs = route.slice(1).map((to, i) => [route[i], to]);
  const result = {
    nodes: new Set(sequence),
    connections: new Set(pairs.map(pair => JSON.stringify(pair))),
    routed: new Set(pairs.filter(([from, to]) => from !== to).map(pair => JSON.stringify(pair))),
  };
  routes.set(instance, result);
  return result;
};

/** Whether drawing every one of these cases stays within the simple map limit. */
export const fitsSimpleMap = (
  instances: ProcessInstance[],
  limit: MapSize = SIMPLE_MAP_LIMIT,
): boolean => {
  const steps = new Set<string>();
  const connections = new Set<string>();
  for (const instance of instances) {
    const route = routeOf(instance);
    for (const node of route.nodes) steps.add(node);
    for (const connection of route.routed) connections.add(connection);
  }
  return withinLimit({ steps: steps.size, connections: connections.size }, limit);
};

const countNew = (features: Set<string>, seen: Set<string>) =>
  [...features].filter(feature => !seen.has(feature)).length;

/**
 * Select complete cases, most typical first, for as long as together they fit a simple map.
 * Each case votes once per distinct node/connection; subtract its own vote so a
 * unique detour cannot support itself. Connections include entry/exit and loops.
 * Average support avoids rewarding length or repeated traversals.
 */
export const selectCommonPaths = (
  instances: ProcessInstance[],
  limit: MapSize = SIMPLE_MAP_LIMIT,
) => {
  const nodeCounts = new Map<string, number>();
  const connectionCounts = new Map<string, number>();
  const paths = instances
    .filter(instance => instance.steps.length > 0)
    .map(instance => {
      const route = routeOf(instance);
      for (const node of route.nodes) nodeCounts.set(node, (nodeCounts.get(node) ?? 0) + 1);
      for (const edge of route.connections)
        connectionCounts.set(edge, (connectionCounts.get(edge) ?? 0) + 1);
      return { instance, ...route };
    });
  if (!paths.length) return { ids: new Set<string>(), isExample: false };

  const support = (features: Set<string>, counts: Map<string, number>) => {
    let sum = 0;
    for (const feature of features) sum += (counts.get(feature) ?? 1) - 1;
    return sum / features.size;
  };
  const scored = paths.map(path => ({
    ...path,
    score:
      (COMMON_PATH_NODE_WEIGHT * support(path.nodes, nodeCounts) +
        COMMON_PATH_CONNECTION_WEIGHT * support(path.connections, connectionCounts)) /
      (COMMON_PATH_NODE_WEIGHT + COMMON_PATH_CONNECTION_WEIGHT),
  }));
  scored.sort(
    (a, b) =>
      b.score - a.score ||
      (a.instance.id < b.instance.id ? -1 : a.instance.id > b.instance.id ? 1 : 0),
  );
  const best = scored[0].score;
  if (best > 0) {
    const ids = new Set<string>();
    const connections = new Set<string>();
    const steps = new Set<string>();
    const routed = new Set<string>();
    for (const path of scored) {
      const added = [...path.connections].filter(edge => !connections.has(edge));
      // A case whose every new connection is its own would only add noise to the map.
      const unsupported = added.length > 0 && added.every(edge => connectionCounts.get(edge) === 1);
      const size: MapSize = {
        steps: steps.size + countNew(path.nodes, steps),
        connections: routed.size + countNew(path.routed, routed),
      };
      const grows = size.steps > steps.size || size.connections > routed.size;
      // Always keep the strongest complete case, whatever its size. A case that no longer fits
      // is skipped rather than ending the fill: a smaller, less typical one may still fit.
      // Cases that draw nothing new cost nothing, so they are never turned away by the limit.
      if (ids.size && (unsupported || (grows && !withinLimit(size, limit)))) continue;
      ids.add(path.instance.id);
      for (const edge of added) connections.add(edge);
      for (const node of path.nodes) steps.add(node);
      for (const connection of path.routed) routed.add(connection);
    }
    return { ids, isExample: ids.size === 1 };
  }

  // With no recurring features, show an honestly labeled example. Median length
  // avoids choosing either a tiny fragment or an unusually long case as the default.
  const ordered = [...paths].sort(
    (a, b) =>
      a.instance.steps.length - b.instance.steps.length ||
      (a.instance.id < b.instance.id ? -1 : a.instance.id > b.instance.id ? 1 : 0),
  );
  return {
    ids: new Set([ordered[Math.floor((ordered.length - 1) / 2)].instance.id]),
    isExample: true,
  };
};
